import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

import { AuthBrandColumn } from "@/components/auth/AuthBrandColumn";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { requestPasswordReset } from "@/services/authService";

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fx-auth-stage">
      <div className="lang-switch">
        <LanguageSwitcher />
      </div>

      <div className="fx-auth-card">
        <div className="fx-auth-form-col">
          <div className="fx-auth-form-inner">
            <AuthBrandMark />
            <span className="fx-auth-eyebrow">
              {submitted
                ? t("auth.forgotPassword.eyebrowSent", "Revisá tu correo")
                : t("auth.forgotPassword.eyebrow", "Recuperá tu acceso")}
            </span>
            <h1 className="fx-auth-h1">
              {submitted ? (
                <>
                  {t("auth.forgotPassword.titleSentLead", "Te enviamos un")}{" "}
                  <span className="ital">
                    {t("auth.forgotPassword.titleSentAccent", "enlace.")}
                  </span>
                </>
              ) : (
                <>
                  {t("auth.forgotPassword.titleLead", "Reseteá tu")}{" "}
                  <span className="ital">
                    {t("auth.forgotPassword.titleAccent", "contraseña.")}
                  </span>
                </>
              )}
            </h1>
            <p className="fx-auth-lead">
              {submitted
                ? t(
                    "auth.forgotPassword.success",
                    "Si existe una cuenta con ese correo, te enviamos un enlace para resetear la contraseña.",
                  )
                : t(
                    "auth.forgotPassword.subtitle",
                    "Ingresá tu correo y te enviamos un enlace para crear una contraseña nueva.",
                  )}
            </p>

            {submitted ? (
              <div className="fx-auth-form">
                <Link to="/login" className="fx-auth-btn fx-auth-btn-ghost">
                  {t("auth.forgotPassword.backToLogin", "Volver a iniciar sesión")}
                </Link>
              </div>
            ) : (
              <form className="fx-auth-form" onSubmit={handleSubmit}>
                <div className="fx-auth-field">
                  <label htmlFor="email">
                    {t("auth.forgotPassword.email", "Correo electrónico")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="vos@email.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="fx-auth-btn fx-auth-btn-primary"
                  disabled={isSubmitting || !email.trim()}
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : (
                    <>
                      {t("auth.forgotPassword.submit", "Enviar enlace")}
                      <ArrowRight aria-hidden />
                    </>
                  )}
                </button>

                <div className="fx-auth-alt" style={{ marginTop: 4 }}>
                  {t("auth.forgotPassword.rememberQuestion", "¿Te acordaste?")}{" "}
                  <Link to="/login">
                    {t("auth.forgotPassword.backToLogin", "Volver a iniciar sesión")}
                    <ArrowRight aria-hidden />
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>

        <AuthBrandColumn mode="login" />
      </div>
    </div>
  );
}
